import { useSelector } from 'react-redux';
import { useQuery } from 'react-apollo';

import {
  AlbumFolderForFeaturePermission,
  AlbumForFeaturePermission,
} from './graphql/queries';
import useSearchSubscriptionFeature from '../../searchSubscriptionFeature';
import { ALBUM_TYPE } from '../../../../../constants/albums';
import { FEATURE_PERMISSION_TYPE, isFreemiumSubtype } from '../helpers';
import usePurchasedEventAddonFeature from '../../purchasedEventAddonFeature';

/**
 * Determines whether a feature is available the current
 * logged in account or not.
 *
 * @param {Object} options The option list.
 * @param {Array<String>} options.features List of IDs of features to check for
 * @param {String?} options.albumId Optional album Id. Used to get to event specific permissions when available.
 * @param {String?} options.albumFolderId Optional folder Id. Used to get to event specific permissions when available.
 * @param {String?} options.purchasedEventId Optional purchased event Id. If provided, event permissions will be forcefully assumed..
 * @param {Array<String>?} options.eventAddonIds Optional event addon Id. If provided, addon permissions will be forcefully assumed..
 * @param {String?} options.eventItemId Optional event item Id. If provided, event permissions will be forcefully assumed..
 * @param {Boolean} options.ownerOnly Whether or not query should only be executed given ownership of the events
 */
const useFeaturePermission = ({
  features = [],
  albumId,
  albumFolderId,
  purchasedEventId,
  eventAddonIds = [],
  eventItemId,
  ownerOnly,
  ...args
} = {}) => {
  const authenticated = useSelector(({ auth }) => auth.authenticated);
  const { loading, data: { albumFolder } = {} } = useQuery(
    AlbumFolderForFeaturePermission,
    {
      skip: !authenticated || !albumFolderId,
      variables: {
        id: albumFolderId,
      },
    },
  );
  const { loading: loadingAlbum, data: { album } = {} } = useQuery(
    AlbumForFeaturePermission,
    {
      skip: !authenticated || !albumId,
      variables: {
        id: albumId,
      },
    },
  );

  const isOwner = album?.is_owner || albumFolder?.is_owner;

  const purchasedEventAddonFeatureItem = usePurchasedEventAddonFeature({
    features,
    // Avoids calling this query for contributors
    albumId: album?.is_owner ? albumId : undefined,
    albumFolderId: albumFolder?.is_owner ? albumFolderId : undefined,
    eventAddonIds,
    ownerOnly,
    ...args,
  });

  const searchSubscriptionFeatureItem = useSearchSubscriptionFeature({
    features,
    queryOptions: {
      skip: ownerOnly && !isOwner,
    },
  });

  // Ensure we don't mess contributors with feature Permissions.
  // These 2 should never be tied up.
  if (albumFolder && !albumFolder.is_owner) {
    return features.reduce((acc, featureId) => {
      return {
        ...acc,
        [featureId]: {
          enabled: true,
        },
      };
    }, {});
  }

  if (albumFolder?.album_type === ALBUM_TYPE.PRO) {
    return features.reduce((acc, featureId) => {
      return {
        ...acc,
        [featureId]: {
          enabled: true,
        },
      };
    }, {});
  }

  const loadingPermissions =
    loading ||
    loadingAlbum ||
    purchasedEventAddonFeatureItem?.loading ||
    searchSubscriptionFeatureItem?.loading;

  if (
    // Un-authed users should never land here - unless we explicitly lock contributor/public
    // features behind a paywall.
    (!authenticated && eventAddonIds.length === 0) ||
    // When loading, return nothing enabled just yet.
    loadingPermissions
  ) {
    return features.reduce(
      (acc, featureId) => {
        return {
          ...acc,
          [featureId]: {
            enabled: false,
          },
        };
      },
      { id: null, loading: loadingPermissions },
    );
  }

  if (
    (album?.album_type === ALBUM_TYPE.EVENT &&
      isFreemiumSubtype(album.album_subtype)) ||
    (albumFolder?.album_type === ALBUM_TYPE.EVENT &&
      isFreemiumSubtype(albumFolder.album_subtype)) ||
    (eventAddonIds ?? []).length > 0
  ) {
    return {
      type: FEATURE_PERMISSION_TYPE.PURCHASED_EVENT_ADDON_FEATURES,
      ...purchasedEventAddonFeatureItem,
    };
  }

  return {
    type: FEATURE_PERMISSION_TYPE.SEARCH_SUBSCRIPTION_FEATURE,
    ...searchSubscriptionFeatureItem,
  };
};

export default useFeaturePermission;
